/**
 * F38 (Wave 2 Track D) — admin subscription tier-catalog page.
 *
 * Route: `/subscription-tiers`
 * Permission: `subscription_tiers:read` (super_admin + support + finance) — view.
 *             `subscription_tiers:write` (super_admin + finance) — edit pricing.
 *
 * Renders the hard-coded 4-tier catalog (Free / Starter / Pro / Premium) with
 * admin-editable pricing + commission. The "Edit pricing" CTA opens
 * `EditTierPricingDialog` (If-Match + mandatory reason); hidden for actors
 * without `subscription_tiers:write`. The feature matrix is shown READ-ONLY
 * from the code-defined `TIER_FEATURE_FLAGS` (D11 — features are not catalog-
 * editable at MVP).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Chip,
  CurrencyDisplay,
  PlanTierBadge,
  Skeleton,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import {
  TIER_FEATURE_FLAGS,
  TIER_FEATURE_FLAGS_KEYS,
  type TierFeatureFlag,
  type SubscriptionTierCode,
} from '@bravemove/types';
import { adminSubscriptionApi, type AdminSubscriptionTier } from '../../api/subscription';
import { translateApiError } from '../../auth/errors';
import { useAuth } from '../../auth/useAuth';
import { PageHeader } from '../../components/PageHeader';
import { EditTierPricingDialog } from '../../components/subscription/EditTierPricingDialog';

function formatRate(rateBp: number): string {
  return `${(rateBp / 100).toFixed(2)}%`;
}

/** One row of the read-only feature matrix (rows = features, cols = tiers). */
interface FeatureRow {
  readonly flag: TierFeatureFlag;
}

export function SubscriptionTiersPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const auth = useAuth();

  const canWrite = auth.adminUser?.permissions?.includes('subscription_tiers:write') ?? false;

  const [tiers, setTiers] = useState<readonly AdminSubscriptionTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<AdminSubscriptionTier | null>(null);

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminSubscriptionApi.listTierCatalog();
      setTiers([...res.data].sort((a, b) => a.sortOrder - b.sortOrder));
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchCatalog();
  }, [fetchCatalog]);

  const catalogColumns = useMemo<readonly TableColumn<AdminSubscriptionTier>[]>(
    () => [
      {
        key: 'tier',
        header: t('admin:subscription.catalog.col_tier', { defaultValue: 'Tier' }),
        cell: (row) => (
          <span data-testid={`tier-name-${row.code}`}>
            <PlanTierBadge
              tier={row.code}
              label={t(`admin:subscription.tier.${row.code}`, { defaultValue: row.displayName })}
            />
          </span>
        ),
      },
      {
        key: 'monthly',
        header: t('admin:subscription.catalog.col_monthly', { defaultValue: 'Monthly' }),
        align: 'end',
        cell: (row) => (
          <span data-testid={`tier-monthly-${row.code}`}>
            <CurrencyDisplay
              amount={row.monthlyPriceAmount}
              currency={row.priceCurrency}
              locale={locale}
            />
          </span>
        ),
      },
      {
        key: 'annual',
        header: t('admin:subscription.catalog.col_annual', { defaultValue: 'Annual' }),
        align: 'end',
        cell: (row) => (
          <span data-testid={`tier-annual-${row.code}`}>
            <CurrencyDisplay
              amount={row.annualPriceAmount}
              currency={row.priceCurrency}
              locale={locale}
            />
          </span>
        ),
      },
      {
        key: 'commission',
        header: t('admin:subscription.catalog.col_commission', { defaultValue: 'Commission' }),
        align: 'end',
        cell: (row) => (
          <span
            className="font-mono text-[13px] text-ink"
            data-testid={`tier-commission-${row.code}`}
          >
            {formatRate(row.commissionBasisPoints)}
          </span>
        ),
      },
      {
        key: 'trial',
        header: t('admin:subscription.catalog.col_trial', { defaultValue: 'Trial default' }),
        cell: (row) =>
          row.isTrialDefault ? (
            <Badge tone="accent" size="sm" data-testid={`tier-trial-${row.code}`}>
              {t('admin:subscription.catalog.trial_default', { defaultValue: 'Trial tier' })}
            </Badge>
          ) : (
            <span className="text-ink-3">—</span>
          ),
      },
      {
        key: 'actions',
        header: t('admin:subscription.catalog.col_actions', { defaultValue: 'Actions' }),
        align: 'end',
        cell: (row) => (
          <div className="flex justify-end">
            {canWrite ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEditTarget(row)}
                data-testid={`tier-edit-${row.code}`}
              >
                {t('admin:subscription.catalog.edit_pricing', { defaultValue: 'Edit pricing' })}
              </Button>
            ) : (
              <Chip data-testid={`tier-readonly-${row.code}`}>
                {t('admin:subscription.catalog.readonly', { defaultValue: 'Read-only' })}
              </Chip>
            )}
          </div>
        ),
      },
    ],
    [t, locale, canWrite],
  );

  const featureRows = useMemo<readonly FeatureRow[]>(
    () => TIER_FEATURE_FLAGS_KEYS.map((flag) => ({ flag })),
    [],
  );

  const tierOrder = useMemo<readonly SubscriptionTierCode[]>(
    () => tiers.map((tt) => tt.code),
    [tiers],
  );

  const featureColumns = useMemo<readonly TableColumn<FeatureRow>[]>(
    () => [
      {
        key: 'feature',
        header: t('admin:subscription.matrix.col_feature', { defaultValue: 'Feature' }),
        cell: (row) => (
          <span className="text-[13px] text-ink">
            {t(`admin:subscription.feature.${row.flag}`, { defaultValue: row.flag })}
          </span>
        ),
      },
      ...tierOrder.map(
        (code): TableColumn<FeatureRow> => ({
          key: code,
          header: t(`admin:subscription.tier.${code}`, { defaultValue: code }),
          align: 'center',
          cell: (row) =>
            TIER_FEATURE_FLAGS[code][row.flag] ? (
              <Badge tone="success" size="sm" data-testid={`feature-${row.flag}-${code}`}>
                {t('admin:subscription.matrix.included', { defaultValue: 'Yes' })}
              </Badge>
            ) : (
              <span className="text-ink-3" data-testid={`feature-${row.flag}-${code}`}>
                —
              </span>
            ),
        }),
      ),
    ],
    [t, tierOrder],
  );

  const onPricingSaved = () => {
    toast.push({
      tone: 'success',
      title: t('admin:subscription.toast.pricing_saved', { defaultValue: 'Tier pricing updated.' }),
    });
    void fetchCatalog();
  };

  return (
    <div className="space-y-6" data-testid="subscription-tiers-page">
      <PageHeader
        title={t('admin:subscription.catalog.title', { defaultValue: 'Subscription tiers' })}
        subtitle={t('admin:subscription.catalog.description', {
          defaultValue:
            'The four subscription tiers. Pricing + commission are admin-editable; features are fixed per tier.',
        })}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="subscription-tiers-loading">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : (
        <>
          <Table
            data={tiers as AdminSubscriptionTier[]}
            columns={catalogColumns as TableColumn<AdminSubscriptionTier>[]}
            rowKey={(row) => row.code}
          />

          <section className="space-y-3" data-testid="subscription-tiers-matrix">
            <h2 className="text-[14px] font-semibold text-ink">
              {t('admin:subscription.matrix.title', { defaultValue: 'Feature matrix (read-only)' })}
            </h2>
            <Banner tone="info">
              {t('admin:subscription.matrix.readonly_note', {
                defaultValue: 'Features are code-defined per tier and not editable here.',
              })}
            </Banner>
            <Table
              data={featureRows as FeatureRow[]}
              columns={featureColumns as TableColumn<FeatureRow>[]}
              rowKey={(row) => row.flag}
            />
          </section>
        </>
      )}

      {editTarget ? (
        <EditTierPricingDialog
          open={editTarget !== null}
          tier={editTarget}
          onConfirm={onPricingSaved}
          onClose={() => setEditTarget(null)}
        />
      ) : null}
    </div>
  );
}

export default SubscriptionTiersPage;
